import { ChatbotBootstrapResponse, ChatbotCourseAsset, TraingeChatbotInitOptions } from "./types";
import { audioFilenameForMimeType } from "./audioMimeTypes";

function resolveApiBase(apiBase?: string) {
  if (apiBase) return apiBase.replace(/\/$/, "");
  return window.location.origin;
}

function serializeWidgetError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  return {
    message: String(error),
  };
}

function logWidgetDiagnostic(
  level: "info" | "error",
  event: string,
  data: Record<string, unknown>
) {
  if (level !== "error") {
    return;
  }

  console.error("[chatbot:widget]", JSON.stringify({ event, ...data }));
}

export class ChatbotApiClient {
  private apiBase: string;

  constructor(private options: TraingeChatbotInitOptions) {
    this.apiBase = resolveApiBase(options.apiBase);
  }

  async bootstrap() {
    const url = `${this.apiBase}/api/chatbot/bootstrap`;
    const startedAt = Date.now();
    logWidgetDiagnostic("info", "bootstrap.request.start", {
      url,
      botId: this.options.botId,
      hasUserId: Boolean(this.options.userId),
      hasVisitorId: Boolean(this.options.visitorId),
      locale: this.options.locale,
    });

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          botId: this.options.botId,
          userId: this.options.userId,
          visitorId: this.options.visitorId,
          brandId: this.options.brandId,
          brandName: this.options.brandName,
          brandWelcome: this.options.brandWelcome,
          locale: this.options.locale,
        }),
      });

      if (!response.ok) throw new Error(await readError(response));

      logWidgetDiagnostic("info", "bootstrap.request.success", {
        url,
        botId: this.options.botId,
        durationMs: Date.now() - startedAt,
        status: response.status,
      });
      return (await response.json()) as ChatbotBootstrapResponse;
    } catch (error) {
      logWidgetDiagnostic("error", "bootstrap.request.error", {
        url,
        botId: this.options.botId,
        durationMs: Date.now() - startedAt,
        error: serializeWidgetError(error),
      });
      throw error;
    }
  }

  async transcribe(audio: Blob) {
    const formData = new FormData();
    formData.append("botId", this.options.botId);
    appendIdentity(formData, this.options);
    formData.append("audio", audio, audioFilenameForMimeType(audio.type));

    const url = `${this.apiBase}/api/chatbot/transcribe`;
    const startedAt = Date.now();
    logWidgetDiagnostic("info", "transcribe.request.start", {
      url,
      botId: this.options.botId,
      audioType: audio.type,
      audioSize: audio.size,
    });

    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error(await readError(response));

      logWidgetDiagnostic("info", "transcribe.request.success", {
        url,
        botId: this.options.botId,
        durationMs: Date.now() - startedAt,
        status: response.status,
      });
      return (await response.json()) as { text: string };
    } catch (error) {
      logWidgetDiagnostic("error", "transcribe.request.error", {
        url,
        botId: this.options.botId,
        durationMs: Date.now() - startedAt,
        error: serializeWidgetError(error),
      });
      throw error;
    }
  }

  async streamChat(input: {
    message: string;
    signal?: AbortSignal;
    onToken: (token: string) => void;
    onCourseAssets?: (courses: ChatbotCourseAsset[]) => void;
    onAssistantMessageId?: (id: string) => void;
  }) {
    const url = `${this.apiBase}/api/chatbot/chat`;
    const startedAt = Date.now();
    logWidgetDiagnostic("info", "chat.request.start", {
      url,
      botId: this.options.botId,
      hasUserId: Boolean(this.options.userId),
      hasVisitorId: Boolean(this.options.visitorId),
      locale: this.options.locale,
      messageChars: input.message.length,
    });

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        botId: this.options.botId,
        userId: this.options.userId,
        visitorId: this.options.visitorId,
        brandId: this.options.brandId,
        brandName: this.options.brandName,
        brandWelcome: this.options.brandWelcome,
        location: this.options.location,
        locale: this.options.locale,
        message: input.message,
      }),
      signal: input.signal,
    });

    if (!response.ok || !response.body) {
      const error = new Error(await readError(response));
      logWidgetDiagnostic("error", "chat.request.error", {
        url,
        botId: this.options.botId,
        durationMs: Date.now() - startedAt,
        status: response.status,
        error: serializeWidgetError(error),
      });
      throw error;
    }

    logWidgetDiagnostic("info", "chat.request.connected", {
      url,
      botId: this.options.botId,
      durationMs: Date.now() - startedAt,
      status: response.status,
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    const processEvent = (event: string) => {
      const lines = event.split("\n");
      const eventType = lines
        .find((item) => item.startsWith("event: "))
        ?.slice(7)
        .trim();
      const line = lines.find((item) => item.startsWith("data: "));
      if (!line) return;
      const data = JSON.parse(line.slice(6));
      if (eventType === "course_assets" && Array.isArray(data.courses)) {
        input.onCourseAssets?.(data.courses);
        return;
      }
      if (eventType === "assistant_message" && typeof data.id === "string") {
        input.onAssistantMessageId?.(data.id);
        return;
      }
      if ("token" in data) input.onToken(data.token);
      if ("message" in data) throw new Error(data.message);
    };

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const event of events) {
          processEvent(event);
        }
      }

      buffer += decoder.decode();
      if (buffer.trim()) {
        processEvent(buffer);
      }

      logWidgetDiagnostic("info", "chat.request.success", {
        url,
        botId: this.options.botId,
        durationMs: Date.now() - startedAt,
      });
    } catch (error) {
      logWidgetDiagnostic("error", "chat.request.error", {
        url,
        botId: this.options.botId,
        durationMs: Date.now() - startedAt,
        error: serializeWidgetError(error),
      });
      throw error;
    }
  }
}

function appendIdentity(formData: FormData, options: TraingeChatbotInitOptions) {
  if (options.userId) {
    formData.append("userId", options.userId);
    return;
  }

  if (options.visitorId) {
    formData.append("visitorId", options.visitorId);
  }
}

async function readError(response: Response) {
  try {
    const data = await response.json();
    return data.error ?? response.statusText;
  } catch {
    return response.statusText;
  }
}
